import { useRouter } from '@tanstack/react-router'
import { AlertCircleIcon, type LucideIcon } from 'lucide-react'
import * as React from 'react'
import { tv, type VariantProps } from 'tailwind-variants'

import { Link } from '@/components'
import { useAuthMutations, useUser } from '@/modules/auth'
import { cn } from '@/utils'

const errorVariants = tv({
  slots: {
    iconWrapper: 'rounded-full p-4',
    icon: 'h-8 w-8',
  },
  variants: {
    theme: {
      default: {
        iconWrapper: 'bg-red-100/70',
        icon: 'text-red-600',
      },
      info: {
        iconWrapper: 'bg-neutral-200/70',
        icon: 'text-muted-foreground',
      },
    },
  },
  defaultVariants: {
    theme: 'default',
  },
})

interface BaseErrorProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof errorVariants> {
  title: string
  description: string
  icon?: LucideIcon
  children?: React.ReactNode
  help?: React.ReactNode
  withLogout?: boolean
}

const BaseError = React.forwardRef<HTMLDivElement, BaseErrorProps>(
  (
    {
      title,
      description,
      icon: Icon,
      theme,
      help,
      withLogout = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const { icon, iconWrapper } = errorVariants({ theme })
    const RenderIcon = Icon ? Icon : AlertCircleIcon
    const router = useRouter()
    const { signOut } = useAuthMutations()
    const user = useUser()

    const handleSignOut = async () => {
      await signOut()
      router.navigate({ to: '/login' })
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex min-h-100 w-full items-center justify-center p-8',
          className,
        )}
        {...props}
      >
        <div className="flex max-w-md flex-col items-center gap-6 text-center">
          <div className={iconWrapper()}>
            <RenderIcon className={icon()} />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
            <p className="text-muted-foreground whitespace-pre-line">
              {description}
            </p>
          </div>

          <div className="flex gap-3">{children}</div>
          {user && withLogout && (
            <Link asChild>
              <button
                type="button"
                className="font-medium"
                onClick={handleSignOut}
              >
                Sign out
              </button>
            </Link>
          )}
        </div>
      </div>
    )
  },
)
BaseError.displayName = 'BaseError'

export { BaseError }
